import { useContext } from 'react'

import { ApiClient } from '../../api/core'
import { invariant } from '../../utils'
import { getApiContext } from '../context'

/**
 * This hook returns an API client instance from the context or an override option.
 * @param {ApiClient} [override] - `override` is an optional parameter of type `ApiClient`. It allows
 * the caller to pass in a custom instance of `ApiClient` to use instead of the one provided by the
 * context. If `override` is not provided, the function will use the `ApiClient` instance provided by
 * the context.
 * @returns The `useApiClient` function returns an instance of the `ApiClient` class. It first checks
 * if an `override` parameter was passed in, and if not, it retrieves the `client` instance from the
 * context using the `getApiContext` function. It then checks if a `client` instance was found, and if
 * not, it throws an error. Finally, it returns the `client
 */
export function useApiClient(override?: ApiClient): ApiClient {
  const context = useContext(getApiContext())
  const client = override || context.client
  invariant(
    !!client,
    'Could not find "client" in the context or passed in as an option. ' +
      'Wrap the root component in an <ApiProvider>, or pass an ApiClient ' +
      'instance in via options.',
  )
  return client
}
